import React from 'react';
import { CheckCircle, AlertCircle, Clock } from 'lucide-react';

const ProgressBar = ({ 
  progress = 0, 
  status = 'pending', 
  fileName = '', 
  fileSize = 0,
  speed = 0,
  timeRemaining = 0,
  showDetails = true 
}) => {
  const getStatusIcon = () => {
    switch (status) {
      case 'success':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'error':
        return <AlertCircle className="h-4 w-4 text-red-500" />;
      case 'uploading':
        return <Clock className="h-4 w-4 text-blue-500 animate-spin" />;
      default:
        return <Clock className="h-4 w-4 text-gray-400" />;
    }
  };

  const getStatusColor = () => {
    switch (status) {
      case 'success':
        return 'bg-green-500';
      case 'error':
        return 'bg-red-500';
      case 'uploading':
        return 'bg-blue-500';
      default:
        return 'bg-gray-400';
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatSpeed = (bytesPerSecond) => {
    if (bytesPerSecond === 0) return '0 B/s';
    return formatFileSize(bytesPerSecond) + '/s';
  };

  const formatTime = (seconds) => {
    if (seconds === 0) return '0s';
    if (seconds < 60) return `${Math.round(seconds)}s`;
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.round(seconds % 60);
    return `${minutes}m ${remainingSeconds}s`;
  };

  return (
    <div className="w-full">
      {/* File info and status */}
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center space-x-2">
          {getStatusIcon()}
          <span className="text-sm font-medium text-gray-900 truncate max-w-xs">
            {fileName}
          </span>
        </div>
        <div className="flex items-center space-x-3 text-xs text-gray-500">
          {fileSize > 0 && (
            <span>{formatFileSize(fileSize)}</span>
          )}
          {status === 'uploading' && speed > 0 && (
            <span>{formatSpeed(speed)}</span>
          )}
          {status === 'uploading' && timeRemaining > 0 && (
            <span>{formatTime(timeRemaining)}</span>
          )}
        </div>
      </div>

      {/* Progress bar */}
      <div className="relative">
        <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
          <div 
            className={`h-full transition-all duration-500 ease-out rounded-full ${getStatusColor()}`}
            style={{ width: `${Math.min(100, Math.max(0, progress))}%` }}
          >
            {/* Animated shimmer effect for uploading */}
            {status === 'uploading' && (
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent opacity-30 animate-pulse"></div>
            )}
          </div>
        </div>
        
        {/* Progress percentage */}
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="text-xs font-medium text-white drop-shadow-sm">
            {Math.round(progress)}%
          </span>
        </div>
      </div>

      {/* Additional details */}
      {showDetails && status === 'uploading' && (
        <div className="mt-2 text-xs text-gray-500">
          <div className="flex justify-between">
            <span>Przesyłanie...</span>
            <span>{Math.round(progress)}% ukończone</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProgressBar;
